<script>
import Logo from '@/Components/Logo'
import SideNav from '@/Components/SideNav'
import MainSearch from '@/Components/MainSearch'
import UserProfile from '@/Components/UserProfile'
import TopIcons from '@/Components/TopIcons'
import HelpDrawer from '@/Components/HelpDrawer'
import FlashMessages from '@/Components/Shared/FlashMessages'
import PageFooter from '@/Components/Footer'
import Notifications from '@/Components/Notifications'
import SubscriptionExpiry from '@/Components/Utilities/SubscriptionExpiry.vue'
import UISwitcher from '../Components/Banners/UISwitcher.vue'
import SimpleModal from '@/Components/Utilities/SimpleModal.vue'
import NewUserInvite from '@/Components/NewUserInvite.vue'
import {Userpilot} from 'userpilot'
import useUser from '../shared/useUser'
import useTenant from '../shared/useTenant'
import {Inertia} from '@inertiajs/inertia'

export default {
    components: {
        SimpleModal,
        SubscriptionExpiry,
        Logo,
        SideNav,
        MainSearch,
        UserProfile,
        TopIcons,
        HelpDrawer,
        FlashMessages,
        PageFooter,
        Notifications,
        UISwitcher,
        NewUserInvite
    },
    data: () => ({
        showNotificationTray: false,
        notificationsCount: 0,
        menuCollapsed: false,
        menuOpen: false,
        windowWidth: 0,
        searchValue: ''
    }),
    computed: {
        collapsedMenuSize() {
            return this.sideMenuCollapsed ? 'w-16' : 'w-56'
        },
        sideMenuCollapsed() {
            return this.menuCollapsed || this.windowWidth < 1024
        },
        isMobile() {
            return this.windowWidth < 640
        },
        isBiggerThanMobile() {
            return this.windowWidth > 639
        },
        isTablet() {
            return (this.isBiggerThanMobile) && (this.windowWidth < 768)
        },
        isBiggerThanTablet() {
            return this.windowWidth >= 768
        },
        user() {
            return useUser().value
        },
        tenant() {
            return useTenant().value
        }
    },
    created() {
        this.windowWidth = window.innerWidth
        window.addEventListener('resize', this.onResize)
    },
    unmounted() {
        window.removeEventListener('resize', this.onResize)
    },
    mounted() {
        this.menuCollapsed = this.$page.props.auth.user.MenuCollapseState

        Userpilot.identify(`${this.user.email}`, {
            name: this.user.fullName,
            first_name: this.user.First,
            last_name: this.user.Last,
            email: this.user.email,
            crm: 'YES',
            created_at: this.user.CreatedDate,
            locale_code: 'en',
            company: {
                id: `CRM-${this.tenant.uuid}`,
                name: this.tenant.name,
                created_at: this.tenant.created_at
            }
        })

        Inertia.on('success', () => {
            Userpilot.reload()
        })
    },
    methods: {
        updateNotificationsCount(newCount) {
            this.notificationsCount = newCount
        },
        setMenuCollapseState() {
            if (this.isBiggerThanTablet) {
                this.menuCollapsed = !this.menuCollapsed
                axios.post(route('menu.collapse', [this.menuCollapsed]))
            } else {
                this.menuOpen = !this.menuOpen

            }
        },
        onResize(e) {
            this.windowWidth = window.innerWidth
        },
        setSearchTerm(value) {
            this.searchValue = value
        }
    },
}
</script>

<template>
    <UISwitcher v-if="$page.props.uiSwitcher" />
    <div class="text-rss-grayblue-600 flex min-h-screen w-full">
        <inertia-head>
            <title>{{ $page.props.appName }}</title>
        </inertia-head>
        <flash-messages />
        <!-- left -->
        <div
            class="bg-rss-dark-blue noPrint shrink-0 transition-all duration-300 ease-in-out"
            :class="[
                (isMobile || isTablet) ? 'fixed inset-y-0 w-56 overflow-auto' : collapsedMenuSize,
            ]"
        >
            <logo
                :menu-collapsed="sideMenuCollapsed"
            />
            <side-nav
                :menu-collapsed="isBiggerThanTablet ? menuCollapsed : false"
                @search="setSearchTerm"
            />
        </div>
        <!-- /left -->

        <!-- right -->
        <div
            class="bg-rss-light-grey relative w-full overflow-hidden transition"
            :class="(isMobile || isTablet) && menuOpen ? 'translate-x-56' : ''"
        >
            <!-- top bar -->
            <div
                class="noPrint flex h-16 items-center justify-between pl-6 shadow-md"
                :class="isBiggerThanMobile ? 'bg-white pl-6' : 'bg-rss-dark-blue px-4'"
            >
                <div
                    class="flex items-center space-x-6"
                    :class="isBiggerThanMobile ? 'w-full' : ''"
                >
                    <div>
                        <button
                            class="hover:text-rss-blue-500 hover:bg-rss-light-blue-500 size-10 rounded-full text-lg focus:outline-none"
                            :class="isBiggerThanMobile ? 'text-rss-grayblue-500' : 'text-white'"
                            @click="setMenuCollapseState()"
                        >
                            <i
                                class="fas"
                                :class="(isMobile || isTablet) && menuOpen ? 'fa-times' : 'fa-bars'"
                            />
                        </button>
                    </div>
                    <main-search
                        v-if="isBiggerThanMobile"
                        :term="searchValue"
                    />
                </div>
                <div v-if="isMobile">
                    <logo />
                </div>
                <div
                    v-if="isBiggerThanMobile"
                    class="flex w-full items-center justify-end space-x-8"
                >
                    <user-profile />
                    <top-icons
                        :notifications-count="notificationsCount"
                        @open-notification-tray="showNotificationTray = true"
                    />
                    <notifications
                        :show="showNotificationTray"
                        @update-notifications-count="updateNotificationsCount"
                        @close-notification-tray="showNotificationTray = false"
                    />
                    <help-drawer />
                </div>
                <div
                    v-else
                    class="justify-end"
                >
                    <user-profile />
                </div>
            </div>
            <!-- /top bar -->

            <!-- content -->
            <div class="p-6">
                <SubscriptionExpiry
                    v-if="$page.props.flash.subscription"
                />
                <SimpleModal
                    v-if="$page.props.flash.modal.show"
                    v-model="$page.props.flash.modal.show"
                    modal-classes="my-auto"
                >
                    <component
                        :is="$page.props.flash.modal.data.component"
                        :="$page.props.flash.modal.data"
                    />
                </SimpleModal>
                <slot />
            </div>
            <!-- /content -->
        </div>
        <!-- right -->
    </div>
    <div class="text-rss-light-text bg-rss-dark-blue noPrint h-14 p-6 text-xs">
        <page-footer />
    </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.25s ease-in-out;
}

</style>
